/**
 * Assistant « Compléter les cas » d'un parcours (vision engagement — lot 2).
 *
 * Après instanciation d'un modèle, les phases existent mais n'ont pas de cas de
 * simulation rattaché. Cet assistant parcourt les phases une par une : pour
 * chaque phase (simulateur + objectif imposés), l'organisateur rattache un cas
 * existant OU en crée un (via SimulationCaseWizard, simulateur pré-verrouillé,
 * parcours de création IA inclus). Réutilise les endpoints existants
 * (updatePhase { targetIds } + getTargets).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, ArrowLeft, ArrowRight, Check, Plus, Sparkles, Loader2, CheckCircle2, Circle } from "lucide-react";
import { certificationPathsApi, accountApi } from "../lib/api";
import { objectiveLabel, objectiveHint } from "../lib/objective-label";
import { Button } from "./ui/button";
import { SimulationCaseWizard } from "./SimulationCaseWizard";

type Phase = {
  id: string; sequence: number; titleFr: string; titleEn?: string | null;
  simulatorId: string; primaryObjective: string; primaryObjectiveTarget: number | null;
  difficulty: string | null; targetIds: string[];
};

interface Props {
  open: boolean;
  phases: Phase[];
  simulators: any[];
  accountId?: string;
  initialPhaseId?: string | null; // ouvrir directement sur cette phase (crayon par phase)
  onClose: () => void;
  onSaved: () => void; // rechargement du détail parcours par le parent
}

export function PathCaseAssistant({ open, phases, simulators, accountId, initialPhaseId, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "fr";

  const ordered = useMemo(() => [...phases].sort((a, b) => a.sequence - b.sequence), [phases]);
  const [idx, setIdx] = useState(0);
  const [targets, setTargets] = useState<any[]>([]);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [wizardSim, setWizardSim] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const loadTargets = () => accountApi.getTargets(accountId).then((tg: any) => setTargets(tg || [])).catch(() => setTargets([]));

  useEffect(() => {
    if (!open) return;
    const start = initialPhaseId ? ordered.findIndex((p) => p.id === initialPhaseId) : 0;
    setIdx(start >= 0 ? start : 0);
    setDirty(false);
    setSelected(Object.fromEntries(ordered.map((p) => [p.id, [...(p.targetIds || [])]])));
    loadTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPhaseId]);

  if (!open) return null;
  const phase = ordered[idx];
  if (!phase) return null;

  const sim = simulators.find((s) => s.id === phase.simulatorId);
  const simName = sim?.name || t("pathAssistant.unknownSimulator");
  const phaseTargets = targets.filter((tg) => tg.simulatorId === phase.simulatorId);
  const sel = selected[phase.id] || [];

  const phaseDone = (p: Phase) => (selected[p.id]?.length ?? 0) > 0;
  const allDone = ordered.every(phaseDone);

  const toggle = (targetId: string) => {
    setDirty(true);
    setSelected((s) => {
      const cur = s[phase.id] || [];
      return { ...s, [phase.id]: cur.includes(targetId) ? cur.filter((x) => x !== targetId) : [...cur, targetId] };
    });
  };

  // Persiste la sélection de la phase courante (idempotent).
  const persistCurrent = async () => {
    await certificationPathsApi.updatePhase(phase.id, { targetIds: selected[phase.id] || [] });
  };

  // Persiste la phase courante puis navigue vers l'index cible (stepper / précédent / suivant).
  const goto = async (target: number) => {
    if (target === idx) return;
    setBusy(true);
    try {
      if (dirty) await persistCurrent();
      setDirty(false);
      setIdx(Math.max(0, Math.min(ordered.length - 1, target)));
    } catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  };

  const goNext = () => goto(idx + 1);

  const finish = async () => {
    setBusy(true);
    try { await persistCurrent(); onSaved(); onClose(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  };

  const targetLabel = (tg: any) => tg.displayName || tg.industry || `${t("pathAssistant.caseFallback")} ${tg.id.slice(0, 6)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 p-0 sm:p-6">
      <div className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-none sm:rounded-2xl bg-white shadow-[var(--elev-3)]">
        {/* En-tête */}
        <div className="flex items-center justify-between border-b px-5 py-3.5" style={{ background: "linear-gradient(90deg,#EEE9FB,#F6F4FD)" }}>
          <div>
            <h2 className="text-base font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("pathAssistant.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("pathAssistant.subtitle")}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Stepper phases */}
          <div className="hidden w-56 shrink-0 overflow-y-auto border-r bg-[#FAFAFC] p-3 sm:block">
            {ordered.map((p, i) => {
              const done = phaseDone(p);
              const active = i === idx;
              return (
                <button key={p.id} onClick={() => goto(i)} disabled={busy}
                  className="mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition"
                  style={{ background: active ? "#EEE9FB" : "transparent", color: active ? "#5A4BBE" : "#384872" }}>
                  {done ? <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "#1E7A3C" }} /> : <Circle className="h-4 w-4 shrink-0 text-gray-300" />}
                  <span className="min-w-0 truncate">{p.sequence}. {p.titleFr}</span>
                </button>
              );
            })}
          </div>

          {/* Panneau phase courante */}
          <div className="min-w-0 flex-1 overflow-y-auto p-5">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide" style={{ color: "#9AA0B0" }}>
              {t("pathAssistant.phaseOf", { current: idx + 1, total: ordered.length })}
            </div>
            <h3 className="text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{phase.titleFr}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full px-2.5 py-1" style={{ background: "#A6D3F9", color: "#384872" }}>{simName}</span>
              <span className="rounded-full px-2.5 py-1" style={{ background: "#F6F4FD", color: "#5A4BBE" }}>
                {objectiveLabel(phase.primaryObjective, sim, t, lang)}
              </span>
              {phase.difficulty && <span className="rounded-full px-2.5 py-1" style={{ background: "#F6C847", color: "#926B00" }}>{t(`training.${phase.difficulty}`, phase.difficulty)}</span>}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{objectiveHint(phase.primaryObjective, phase.primaryObjectiveTarget, t)}</p>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: "#384872" }}>{t("pathAssistant.attachCase")}</span>
                {sel.length > 0 && <span className="text-xs" style={{ color: "#1E7A3C" }}>{t("pathAssistant.attachedCount", { count: sel.length })}</span>}
              </div>

              {phaseTargets.length === 0 ? (
                <div className="rounded-xl border border-dashed p-5 text-center">
                  <p className="text-sm text-muted-foreground">{t("pathAssistant.noCaseForSim", { simulator: simName })}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {phaseTargets.map((tg) => {
                    const checked = sel.includes(tg.id);
                    return (
                      <button key={tg.id} onClick={() => toggle(tg.id)}
                        className="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition hover:shadow-sm"
                        style={{ borderColor: checked ? "#5A4BBE" : "#E6E4EE", background: checked ? "#F6F4FD" : "white" }}>
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border"
                          style={{ borderColor: checked ? "#5A4BBE" : "#CBD0DC", background: checked ? "#5A4BBE" : "white" }}>
                          {checked && <Check className="h-3.5 w-3.5 text-white" />}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium" style={{ color: "#384872" }}>{targetLabel(tg)}</div>
                          {(tg.offerName || tg.industry) && <div className="truncate text-xs text-muted-foreground">{[tg.offerName, tg.industry].filter(Boolean).join(" · ")}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setWizardSim(phase.simulatorId)}>
                  <Plus className="mr-1 h-4 w-4" />{t("pathAssistant.createCase")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setWizardSim(phase.simulatorId)}>
                  <Sparkles className="mr-1 h-4 w-4" style={{ color: "#5A4BBE" }} />{t("pathAssistant.createCaseAi")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Pied de navigation */}
        <div className="flex items-center justify-between border-t px-5 py-3">
          <Button variant="ghost" size="sm" onClick={() => idx > 0 ? goto(idx - 1) : onClose()} disabled={busy}>
            <ArrowLeft className="mr-1 h-4 w-4" />{idx > 0 ? t("common.back") : t("common.cancel")}
          </Button>
          <div className="flex items-center gap-2">
            {idx < ordered.length - 1 ? (
              <Button size="sm" onClick={goNext} disabled={busy}>
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t("pathAssistant.saveNext")}<ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button size="sm" onClick={finish} disabled={busy} style={{ backgroundColor: allDone ? undefined : "#384872" }}>
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                {t("pathAssistant.finish")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Création d'un cas — simulateur imposé par la phase */}
      <SimulationCaseWizard
        open={wizardSim !== null}
        presetSimulatorId={wizardSim ?? undefined}
        accountId={accountId}
        onClose={() => setWizardSim(null)}
        onCreated={() => { setWizardSim(null); loadTargets(); }}
      />
    </div>
  );
}
